/* Claude v1 — Marsela Design storefront behaviour */
(() => {
  'use strict';

  // routes.root_url is "/" in English but "/de" (no trailing slash) in translated locales.
  const root = (document.body.dataset.marselaRoot || '/').replace(/\/?$/, '/');
  // English defaults; the store language's copy comes from window.MarselaStrings.general
  // (locales/*.json, js.general), rendered by layout/theme.liquid.
  const STRINGS = {searchEmpty: 'No designs match “{term}”. Try a theme such as woodland, safari or moon.', searchAll: 'See all results for “{term}”', shopTheme: 'Shop {label} murals', titleKeyword: ''};
  const t = key => window.MarselaStrings?.general?.[key] ?? STRINGS[key];
  const reduceMotion = window.matchMedia('(prefers-reduced-motion: reduce)');
  const desktop = window.matchMedia('(min-width: 990px)');
  const $ = (selector, scope = document) => scope.querySelector(selector);
  const $$ = (selector, scope = document) => [...scope.querySelectorAll(selector)];

  /* ---------- Dialogs: menu drawer, search ---------- */
  function openDialog(dialog, trigger) {
    if (!dialog || dialog.open) return;
    dialog._trigger = trigger || null;
    dialog.showModal();
    document.body.classList.add('is-locked');
    $$(`[aria-controls="${dialog.id}"]`).forEach(button => button.setAttribute('aria-expanded', 'true'));
    const focusTarget = $('[data-search-input]', dialog);
    if (focusTarget) requestAnimationFrame(() => focusTarget.focus());
  }
  function bindDialogs(scope = document) {
    $$('[data-open-dialog]', scope).forEach(trigger => {
      if (trigger.dataset.bound) return;
      trigger.dataset.bound = 'true';
      trigger.addEventListener('click', () => openDialog(document.getElementById(trigger.dataset.openDialog), trigger));
    });
    $$('dialog[data-dialog]', scope).forEach(dialog => {
      if (dialog.dataset.bound) return;
      dialog.dataset.bound = 'true';
      $$('[data-close-dialog]', dialog).forEach(button => button.addEventListener('click', () => dialog.close()));
      dialog.addEventListener('click', event => {
        if (event.target === dialog) dialog.close();
        if (event.target.closest('a[href]')) dialog.close();
      });
      dialog.addEventListener('close', () => {
        document.body.classList.remove('is-locked');
        $$(`[aria-controls="${dialog.id}"]`).forEach(button => button.setAttribute('aria-expanded', 'false'));
        dialog._trigger?.focus({ preventScroll: true });
      });
    });
  }

  /* ---------- Mega menus ---------- */
  function bindMega(scope = document) {
    const items = $$('[data-mega]', scope);
    if (!items.length) return;
    const closeAll = except => items.forEach(item => {
      if (item === except) return;
      item.classList.remove('is-open');
      $('.mw-nav__link', item)?.setAttribute('aria-expanded', 'false');
    });
    const open = item => {
      closeAll(item);
      item.classList.add('is-open');
      $('.mw-nav__link', item)?.setAttribute('aria-expanded', 'true');
    };
    items.forEach(item => {
      if (item.dataset.bound) return;
      item.dataset.bound = 'true';
      const button = $('.mw-nav__link', item);
      let timer;
      button.addEventListener('click', () => item.classList.contains('is-open') ? closeAll() : open(item));
      item.addEventListener('pointerenter', event => {
        if (event.pointerType !== 'mouse') return;
        clearTimeout(timer);
        timer = setTimeout(() => open(item), 90);
      });
      item.addEventListener('pointerleave', event => {
        if (event.pointerType !== 'mouse') return;
        clearTimeout(timer);
        timer = setTimeout(() => { item.classList.remove('is-open'); button.setAttribute('aria-expanded', 'false'); }, 160);
      });
      item.addEventListener('keydown', event => {
        if (event.key === 'Escape') { closeAll(); button.focus(); }
      });
      item.addEventListener('focusout', event => {
        if (!item.contains(event.relatedTarget)) { item.classList.remove('is-open'); button.setAttribute('aria-expanded', 'false'); }
      });
    });
    document.addEventListener('click', event => { if (!event.target.closest('[data-mega]')) closeAll(); });
  }

  /* ---------- Header: hide on scroll down, show on scroll up ---------- */
  function bindHeader() {
    const section = $('[data-header]')?.closest('.shopify-section');
    if (!section || section.dataset.bound) return;
    section.dataset.bound = 'true';
    section.classList.add('mw-header-section');
    let last = window.scrollY, ticking = false;
    const update = () => {
      const y = window.scrollY;
      const megaOpen = $('[data-mega].is-open');
      section.classList.toggle('is-scrolled', y > 8);
      const hide = y > 260 && y > last + 4 && !megaOpen && !document.body.classList.contains('is-locked');
      const show = y < last - 4 || y < 140;
      if (hide) { section.classList.add('is-hidden'); document.body.classList.add('header-hidden'); }
      else if (show) { section.classList.remove('is-hidden'); document.body.classList.remove('header-hidden'); }
      last = y;
      ticking = false;
    };
    window.addEventListener('scroll', () => { if (!ticking) { requestAnimationFrame(update); ticking = true; } }, { passive: true });
    update();
  }

  /* ---------- Announcement rotation (phones) ---------- */
  function bindAnnouncement() {
    const bar = $('[data-rotating]');
    if (!bar || bar.dataset.bound) return;
    bar.dataset.bound = 'true';
    const items = $$('.mw-announce__item', bar);
    if (items.length < 2) return;
    let index = 0;
    setInterval(() => {
      if (document.hidden || window.innerWidth >= 750) return;
      items[index].classList.remove('is-current');
      index = (index + 1) % items.length;
      items[index].classList.add('is-current');
    }, 5000);
  }

  /* ---------- Predictive search ---------- */
  function bindSearch() {
    const dialog = $('[data-search]');
    if (!dialog || dialog.dataset.searchBound) return;
    dialog.dataset.searchBound = 'true';
    const input = $('[data-search-input]', dialog);
    const results = $('[data-search-results]', dialog);
    const hints = $('[data-search-hints]', dialog);
    let timer, controller;
    const escape = value => String(value).replace(/[&<>"']/g, character => ({ '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#39;' }[character]));
    // "Soft Green Forest Wall Mural" -> "Soft Green Forest"; a translated title starts with the
    // locale's keyword instead ("Fototapete Sanftgrüner Wald" -> "Sanftgrüner Wald").
    const keyword = t('titleKeyword');
    const shortTitle = title => { const name = title.split(' — ')[0].replace(/ Wall(paper)? Mural$/, ''); return keyword && name.startsWith(`${keyword} `) ? name.slice(keyword.length + 1) : name; };
    async function run(term) {
      controller?.abort();
      if (term.length < 2) { results.innerHTML = ''; hints.hidden = false; return; }
      controller = new AbortController();
      try {
        const url = `${root}search/suggest.json?q=${encodeURIComponent(term)}&resources[type]=product&resources[limit]=10&resources[options][unavailable_products]=last`;
        const response = await fetch(url, { signal: controller.signal, headers: { Accept: 'application/json' } });
        if (!response.ok) throw new Error('search');
        const data = await response.json();
        const products = (data.resources?.results?.products || []).filter(item => !/^marsela-(quote|wallpaper-sample)/.test(item.handle)).slice(0, 5);
        hints.hidden = products.length > 0;
        const all = `${root}search?type=product&q=${encodeURIComponent(term)}`;
        if (!products.length) {
          results.innerHTML = `<p class="mw-muted">${escape(t('searchEmpty')).replace('{term}', escape(term))}</p>`;
          return;
        }
        results.innerHTML = `<div class="mw-search__results-grid">${products.map(item => {
          const image = item.featured_image?.url || item.image;
          const src = image ? `${image}${image.includes('?') ? '&' : '?'}width=360` : '';
          return `<a class="mw-search__result" href="${escape(item.url)}">${src ? `<img src="${escape(src)}" alt="" width="360" height="480" loading="lazy">` : ''}<span>${escape(shortTitle(item.title))}</span></a>`;
        }).join('')}</div><a class="mw-link mw-search__all" href="${all}">${escape(t('searchAll')).replace('{term}', escape(term))}</a>`;
      } catch (error) {
        if (error.name !== 'AbortError') results.innerHTML = '';
      }
    }
    input.addEventListener('input', () => {
      clearTimeout(timer);
      timer = setTimeout(() => run(input.value.trim()), 220);
    });
  }

  /* ---------- Reveal on scroll ---------- */
  function bindReveal(scope = document) {
    const targets = $$('[data-reveal], .mw-how', scope);
    if (!targets.length) return;
    if (reduceMotion.matches || !('IntersectionObserver' in window)) { targets.forEach(target => target.classList.add('is-revealed')); return; }
    const observer = new IntersectionObserver(entries => entries.forEach(entry => {
      if (!entry.isIntersecting) return;
      entry.target.classList.add('is-revealed');
      observer.unobserve(entry.target);
    }), { rootMargin: '0px 0px -12% 0px', threshold: .12 });
    targets.forEach(target => observer.observe(target));
  }

  /* ---------- Hero arch windows ---------- */
  function bindHero(scope = document) {
    $$('[data-arches]', scope).forEach(arches => {
      if (arches.dataset.bound) return;
      arches.dataset.bound = 'true';
      const frames = $$('[data-arch-frame]', arches);
      if (reduceMotion.matches) return;
      let step = 0;
      const cycle = () => {
        if (document.hidden) return;
        const frame = frames[step % frames.length];
        step += 1;
        const images = $$('img', frame);
        if (images.length < 2) return;
        const current = images.findIndex(image => image.classList.contains('is-current'));
        const next = (current + 1) % images.length;
        const incoming = images[next];
        const swap = () => {
          images[current].classList.remove('is-current');
          incoming.classList.add('is-current');
          const link = $('[data-arch-link]', frame.parentElement);
          if (link && incoming.dataset.href) { link.href = incoming.dataset.href; if (incoming.dataset.label) link.setAttribute('aria-label', t('shopTheme').replace('{label}', incoming.dataset.label)); }
          const label = $('[data-arch-label]', frame.parentElement);
          if (label && incoming.dataset.label) {
            label.style.opacity = '0';
            setTimeout(() => { label.textContent = incoming.dataset.label; label.style.opacity = ''; }, 500);
          }
        };
        if (incoming.complete) swap(); else incoming.addEventListener('load', swap, { once: true });
      };
      setTimeout(() => setInterval(cycle, 2600), 3200);
    });
  }

  /* ---------- Rails ---------- */
  function bindRails(scope = document) {
    $$('[data-rail]', scope).forEach(rail => {
      if (rail.dataset.bound) return;
      rail.dataset.bound = 'true';
      const track = $('[data-rail-track]', rail);
      const prev = $('[data-rail-prev]', rail), next = $('[data-rail-next]', rail);
      if (!track || !prev || !next) return;
      const update = () => {
        prev.disabled = track.scrollLeft < 8;
        next.disabled = track.scrollLeft + track.clientWidth > track.scrollWidth - 8;
      };
      const page = direction => track.scrollBy({ left: direction * track.clientWidth * .8, behavior: reduceMotion.matches ? 'auto' : 'smooth' });
      prev.addEventListener('click', () => page(-1));
      next.addEventListener('click', () => page(1));
      track.addEventListener('scroll', () => requestAnimationFrame(update), { passive: true });
      window.addEventListener('resize', update);
      update();
    });
  }

  /* ---------- Product gallery + lightbox ---------- */
  function bindGallery(scope = document) {
    $$('[data-gallery]', scope).forEach(gallery => {
      if (gallery.dataset.bound) return;
      gallery.dataset.bound = 'true';
      const track = $('[data-gallery-track]', gallery);
      const slides = $$('[data-gallery-slide]', gallery);
      const dots = $$('[data-gallery-dot]', gallery);
      const count = $('[data-gallery-count]', gallery);
      const lightbox = $('[data-lightbox]', gallery.parentElement) || $('[data-lightbox]');
      let current = 0;
      const idle = callback => ('requestIdleCallback' in window ? requestIdleCallback(callback, { timeout: 2500 }) : setTimeout(callback, 800));
      const warm = () => slides.forEach(slide => { const photo = $('img', slide); if (photo && photo.loading === 'lazy') photo.loading = 'eager'; });
      if (document.readyState === 'complete') idle(warm); else window.addEventListener('load', () => idle(warm), { once: true });
      const setCurrent = index => {
        current = index;
        dots.forEach((dot, i) => dot.setAttribute('aria-current', String(i === index)));
        if (count) count.textContent = `${index + 1} / ${slides.length}`;
      };
      if (track && 'IntersectionObserver' in window) {
        const observer = new IntersectionObserver(entries => entries.forEach(entry => {
          if (entry.isIntersecting && !desktop.matches) setCurrent(slides.indexOf(entry.target));
        }), { root: track, threshold: .6 });
        slides.forEach(slide => observer.observe(slide));
      }
      dots.forEach((dot, index) => dot.addEventListener('click', () => {
        slides[index].scrollIntoView({ behavior: reduceMotion.matches ? 'auto' : 'smooth', block: 'nearest', inline: 'center' });
      }));
      if (!lightbox) return;
      const image = $('[data-lightbox-image]', lightbox);
      const counter = $('[data-lightbox-count]', lightbox);
      const fullPhotos = new Map();
      const preload = index => {
        const url = slides[(index + slides.length) % slides.length]?.dataset.full;
        if (!url || fullPhotos.has(url)) return fullPhotos.get(url);
        const photo = new Image(); photo.decoding = 'async'; photo.src = url; fullPhotos.set(url, photo); return photo;
      };
      const show = index => {
        current = (index + slides.length) % slides.length;
        const slide = slides[current];
        const full = slide.dataset.full, photo = preload(current), onPage = $('img', slide)?.currentSrc;
        if (photo?.complete && photo.naturalWidth) image.src = full;
        else {
          if (onPage) image.src = onPage;
          const swap = () => { if (slides[current] === slide) image.src = full; };
          (photo?.decode ? photo.decode() : Promise.reject()).then(swap, swap);
        }
        preload(current + 1); preload(current - 1);
        image.alt = $('img', slide)?.alt || '';
        if (counter) counter.textContent = `${current + 1} / ${slides.length}`;
      };
      $$('[data-gallery-zoom]', gallery).forEach(button => button.addEventListener('click', () => {
        show(Number(button.dataset.galleryZoom));
        lightbox.showModal();
        idle(() => slides.forEach((_, i) => preload(i)));
        document.body.classList.add('is-locked');
      }));
      $('[data-lightbox-prev]', lightbox)?.addEventListener('click', () => show(current - 1));
      $('[data-lightbox-next]', lightbox)?.addEventListener('click', () => show(current + 1));
      $('[data-lightbox-close]', lightbox)?.addEventListener('click', () => lightbox.close());
      lightbox.addEventListener('close', () => document.body.classList.remove('is-locked'));
      lightbox.addEventListener('keydown', event => {
        if (event.key === 'ArrowLeft') show(current - 1);
        if (event.key === 'ArrowRight') show(current + 1);
      });
      let startX = null;
      lightbox.addEventListener('pointerdown', event => { startX = event.clientX; });
      lightbox.addEventListener('pointerup', event => {
        if (startX === null) return;
        const delta = event.clientX - startX;
        if (Math.abs(delta) > 50) show(current + (delta < 0 ? 1 : -1));
        startX = null;
      });
    });
  }

  /* ---------- Sticky buy bar (phones) ---------- */
  function bindStickyBuy(scope = document) {
    const bar = $('[data-sticky-buy]', scope);
    const anchor = $('[data-buy-anchor]', scope);
    if (!bar || !anchor || bar.dataset.bound) return;
    bar.dataset.bound = 'true';
    const footer = $('.mw-footer');
    let anchorVisible = true, footerVisible = false;
    const render = () => {
      const visible = !anchorVisible && !footerVisible && anchor.getBoundingClientRect().top < 0;
      bar.classList.toggle('is-visible', visible);
      document.body.classList.toggle('has-sticky-buy', visible);
      bar.setAttribute('aria-hidden', String(!visible));
      $$('a, button', bar).forEach(element => { element.tabIndex = visible ? 0 : -1; });
    };
    const observer = new IntersectionObserver(entries => entries.forEach(entry => {
      if (entry.target === anchor) anchorVisible = entry.isIntersecting;
      else footerVisible = entry.isIntersecting;
      render();
    }));
    observer.observe(anchor);
    if (footer) observer.observe(footer);
    let queued = false;
    window.addEventListener('scroll', () => {
      if (queued) return;
      queued = true;
      requestAnimationFrame(() => { queued = false; render(); });
    }, { passive: true });
    $('[data-sticky-target]', bar)?.addEventListener('click', event => {
      const target = document.getElementById(event.currentTarget.dataset.stickyTarget);
      if (!target) return;
      event.preventDefault();
      target.scrollIntoView({ behavior: reduceMotion.matches ? 'auto' : 'smooth', block: 'center' });
      const field = $('input:not([type=hidden]), select, textarea', target);
      setTimeout(() => field?.focus({ preventScroll: true }), reduceMotion.matches ? 0 : 500);
    });
    render();
  }

  /* ---------- Before / after ---------- */
  function bindCompare(scope = document) {
    $$('[data-compare]', scope).forEach(compare => {
      if (compare.dataset.bound) return;
      compare.dataset.bound = 'true';
      const range = $('input[type=range]', compare);
      const set = value => compare.style.setProperty('--pos', `${value}%`);
      range.addEventListener('input', () => set(range.value));
      set(range.value);
    });
  }

  /* ---------- Collection sort ---------- */
  function bindSort(scope = document) {
    $$('[data-sort]', scope).forEach(select => {
      if (select.dataset.bound) return;
      select.dataset.bound = 'true';
      select.addEventListener('change', () => {
        const url = new URL(location.href);
        url.searchParams.set('sort_by', select.value);
        url.searchParams.delete('page');
        location.assign(url.toString());
      });
    });
  }

  /* ---------- Keep the active chip in view ---------- */
  function bindChips(scope = document) {
    $$('.mw-chips', scope).forEach(row => {
      const active = $('[aria-current="page"]', row);
      if (active && row.scrollWidth > row.clientWidth) row.scrollLeft = active.offsetLeft - (row.clientWidth - active.offsetWidth) / 2;
    });
  }

  /* ---------- Product info column: only sticky when it fits on screen ---------- */
  function bindStickyInfo(scope = document) {
    $$('.mw-product__info', scope).forEach(info => {
      if (info.dataset.stickyFit) return;
      info.dataset.stickyFit = 'true';
      const fit = () => {
        const header = parseFloat(getComputedStyle(document.documentElement).getPropertyValue('--header-h')) || 80;
        info.classList.toggle('is-tall', info.offsetHeight > window.innerHeight - header - 40);
      };
      fit();
      window.addEventListener('resize', fit, {passive: true});
      if ('ResizeObserver' in window) new ResizeObserver(fit).observe(info);
    });
  }

  /* ---------- Delivery estimate: business-day date range ---------- */
  function bindDelivery(scope = document) {
    $$('[data-ms-delivery]', scope).forEach(box => {
      const out = $('[data-ms-delivery-range]', box);
      if (!out) return;
      const n = key => Math.max(0, parseInt(box.dataset[key], 10) || 0);
      const addBusinessDays = (date, days) => {
        const d = new Date(date);
        while (days > 0) { d.setDate(d.getDate() + 1); if (d.getDay() !== 0 && d.getDay() !== 6) days--; }
        return d;
      };
      const today = new Date();
      const earliest = addBusinessDays(today, n('productionMin') + n('transitMin'));
      const latest = addBusinessDays(today, n('productionMax') + n('transitMax'));
      const fmt = new Intl.DateTimeFormat(document.documentElement.lang || 'en-US', {month: 'short', day: 'numeric'});
      out.textContent = `${fmt.format(earliest)} – ${fmt.format(latest)}`;
    });
  }

  /* ---------- Cart: show each mural's saved crop ---------- */
  function bindCartCrops(scope = document) {
    $$('[data-ms-cart-crop]', scope).forEach(frame => {
      if (frame.dataset.cropReady) return;
      let config;
      try { config = JSON.parse(frame.dataset.msCartCrop); } catch (_) { return; }
      const crop = config && config.crop, wall = config && config.wall;
      const ok = crop && wall && ['x', 'y', 'width', 'height'].every(k => Number.isFinite(crop[k])) && crop.width > 0 && crop.height > 0 && wall.widthCm > 0 && wall.heightCm > 0;
      if (!ok) return;
      const tile = $('.ms-cart__crop', frame);
      // The saved configuration names the exact design: use its artwork and product page.
      const art = config.artwork || {};
      let source = null;
      try { source = art.sourceUrl ? new URL(art.sourceUrl, location.href) : null; } catch (_) { source = null; }
      if (source && source.protocol === 'https:' && (source.hostname === 'cdn.shopify.com' || source.hostname === location.hostname)) {
        if (source.hostname === 'cdn.shopify.com' && !source.searchParams.has('width')) source.searchParams.set('width', '900');
        tile.style.backgroundImage = `url("${source.href.replace(/"/g, '%22')}")`;
      }
      const handle = typeof config.productHandle === 'string' && /^[a-z0-9]+(?:-[a-z0-9]+)*$/.test(config.productHandle) ? config.productHandle : '';
      const item = frame.closest('.ms-cart__item');
      // The cart template already links known designs to their localized page; only a link
      // that still points at the hidden quote product is sent to the design instead.
      if (handle && item) $$('[data-ms-cart-link]', item).forEach(link => { if (/\/products\/marsela-quote-/.test(link.getAttribute('href') || '')) link.href = `${root}products/${handle}`; });
      const pos = (offset, size) => size >= 1 ? 50 : offset / (1 - size) * 100;
      const aspect = wall.widthCm / wall.heightCm;
      tile.style.aspectRatio = `${wall.widthCm} / ${wall.heightCm}`;
      tile.style.width = aspect >= .75 ? '100%' : 'auto';
      tile.style.height = aspect >= .75 ? 'auto' : '100%';
      tile.style.backgroundSize = `${100 / crop.width}% ${100 / crop.height}%`;
      tile.style.backgroundPosition = `${pos(crop.x, crop.width)}% ${pos(crop.y, crop.height)}%`;
      tile.classList.toggle('is-mirrored', config.mirror === true);
      frame.dataset.cropReady = 'true';
    });
  }

  /* ---------- Guide films ---------- */
  function bindFilms(scope = document) {
    const play = video => { const attempt = video.play(); if (attempt && attempt.catch) attempt.catch(() => {}); };
    $$('dialog[data-ms-film-dialog]', scope).forEach(dialog => {
      if (dialog.dataset.filmBound) return;
      dialog.dataset.filmBound = 'true';
      const video = $('video', dialog);
      if (!video) return;
      dialog.addEventListener('close', () => video.pause());
      $$(`[data-open-dialog="${dialog.id}"]`).forEach(trigger => trigger.addEventListener('click', () => {
        requestAnimationFrame(() => { if (dialog.open) play(video); });
      }));
    });
    $$('video[data-ms-guide-video]', scope).forEach(video => {
      if (video.dataset.filmBound) return;
      video.dataset.filmBound = 'true';
      video.addEventListener('play', () => $$('video[data-ms-guide-video]').forEach(other => { if (other !== video && !other.paused) other.pause(); }));
    });
  }

  function init(scope = document) {
    bindCartCrops(scope);
    bindStickyInfo(scope);
    bindDelivery(scope);
    bindChips(scope);
    bindDialogs(scope);
    bindFilms(scope);
    bindMega(scope);
    bindReveal(scope);
    bindHero(scope);
    bindRails(scope);
    bindGallery(scope);
    bindStickyBuy(scope);
    bindCompare(scope);
    bindSort(scope);
  }

  bindHeader();
  bindAnnouncement();
  bindSearch();
  init();
  document.addEventListener('shopify:section:load', event => { init(event.target); bindHeader(); bindSearch(); });
})();
